import { combineReducers, configureStore, Tuple } from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query'
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE
} from 'redux-persist'
import storage from 'redux-persist/es/storage'
import { apiMiddleware } from 'redux-api-middleware'

import { apiSlice } from 'app/services/api'
import filterProfilesReducer from 'app/entities/store/filterProfilesSlice'
import linesReducer from 'app/entities/store/linesSlice'
import modalsReducer from 'app/entities/store/modalsSlice'
import networksReducer from 'app/entities/store/networksSlice'
import snackbarReducer from 'app/entities/store/snackbarSlice'
import globalFilterReducer from 'app/layout/Navbar/Filters/globalFilterSlice'

import whatIfScenarioSliceReducer from 'features/RiskManagement/Assessments/Scenarios/whatIfScenarioSlice'
import qraSavedStateSliceReducer from 'features/RiskManagement/Assessments/States/qraSavedStateSlice'
import localFilterReducer from 'features/RiskManagement/Components/LocalFilter/localFilterSlice'
import settingsReducer from 'features/Settings/settingsSlice'

import authTokenMiddleware from './middlewares/authToken'
import notificationMiddleware from './middlewares/notification'

const persistConfig = {
  key: 'root',
  version: 1,
  storage,
  whitelist: ['localFilter']
}

const rootReducer = combineReducers({
  entities: combineReducers({
    filterProfiles: filterProfilesReducer,
    networks: networksReducer,
    lines: linesReducer
  }),
  [apiSlice.reducerPath]: apiSlice.reducer,
  riskManagement: combineReducers({
    savedState: qraSavedStateSliceReducer,
    whatIfScenario: whatIfScenarioSliceReducer
  }),
  globalFilter: globalFilterReducer,
  localFilter: localFilterReducer,
  settings: settingsReducer,
  snackbar: snackbarReducer,
  modals: modalsReducer
})

export const createStore = () =>
  configureStore({
    devTools: import.meta.env.VITE_ENABLE_REDUX_DEVTOOLS === 'true',
    reducer: persistReducer<ReturnType<typeof rootReducer>>(
      persistConfig,
      rootReducer
    ),
    middleware: (getDefaultMiddleware) => {
      return (
        getDefaultMiddleware({
          thunk: true,
          serializableCheck: {
            ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
          }
        })
          // NOTE: apiMiddleware breaks Tuple type and requires later casting
          .prepend([authTokenMiddleware, apiMiddleware])
          .concat([notificationMiddleware, apiSlice.middleware]) as Tuple
      )
    }
  })

export const store = createStore()

setupListeners(store.dispatch)

export const persistor = persistStore(store)

export type RootState = ReturnType<typeof store.getState>

export type AppDispatch = typeof store.dispatch
